import { menu, pizza, images, restaurant } from "../data";
import { Phone } from "lucide-react";
import { useReveal } from "../hooks/useReveal";

const byNum = (a, b) => {
  const na = a.num ? parseInt(a.num, 10) : Infinity;
  const nb = b.num ? parseInt(b.num, 10) : Infinity;
  return na - nb;
};

const sorted = (items) => [...items].sort(byNum);

function MenuItem({ item, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-baseline gap-3 py-3 border-b border-white/5 last:border-0 group"
    >
      {item.num && (
        <span className="flex-shrink-0 text-xs font-display font-bold text-[#e05d36]/70 w-8 pt-0.5">
          {item.num}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <h4 className="font-semibold text-[#f5f5f0] group-hover:text-[#e05d36] transition-colors duration-200">
          {item.name}
        </h4>
        <p className="text-sm text-[#a3a3a3] leading-snug">{item.desc}</p>
      </div>
      <span className="font-display font-bold text-[#eab308] whitespace-nowrap">{item.price} €</span>
    </div>
  );
}

function FeaturedCard({ cat, image, testidBase }) {
  return (
    <div className="rounded-2xl overflow-hidden bg-[#1a1a1a] border border-white/10 grid lg:grid-cols-12">
      <div className="lg:col-span-4 relative min-h-[240px]">
        <img src={image} alt={cat.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#1a1a1a]" />
        {cat.note && (
          <span className="absolute top-4 left-4 font-script text-2xl text-[#eab308] rotate-[-4deg]">{cat.note}</span>
        )}
      </div>
      <div className="lg:col-span-8 p-6 md:p-8">
        <h3 className="font-display font-black text-2xl text-[#f5f5f0]">{cat.title}</h3>
        {cat.desc && <p className="mt-1 text-xs text-[#a3a3a3] leading-snug max-w-2xl">{cat.desc}</p>}
        <div className="mt-4 grid sm:grid-cols-2 gap-x-8">
          {sorted(cat.items).map((it) => (
            <MenuItem key={it.num || it.name} item={it} testid={`${testidBase}-${it.num || it.name}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TextCard({ cat, testidBase }) {
  return (
    <div className="rounded-2xl bg-[#1a1a1a] border border-white/10 p-6 md:p-7 h-full">
      <h3 className="font-display font-black text-xl text-[#f5f5f0]">{cat.title}</h3>
      {cat.desc && <p className="mt-1 text-xs text-[#a3a3a3] leading-snug">{cat.desc}</p>}
      <div className="mt-3">
        {sorted(cat.items).map((it) => (
          <MenuItem key={it.num || it.name} item={it} testid={`${testidBase}-${it.num || it.name}`} />
        ))}
      </div>
    </div>
  );
}

function PizzaRow({ item }) {
  return (
    <div
      data-testid={`menu-pizza-${item.num}`}
      className="grid grid-cols-[2rem_1fr_auto] gap-x-3 items-baseline py-3 border-b border-white/5 last:border-0 group"
    >
      <span className="text-xs font-display font-bold text-[#e05d36]/70 pt-0.5">{item.num}</span>
      <div className="min-w-0">
        <h4 className="font-semibold text-[#f5f5f0] group-hover:text-[#e05d36] transition-colors duration-200">
          {item.name}
        </h4>
        <p className="text-sm text-[#a3a3a3] leading-snug">{item.desc}</p>
      </div>
      <div className="flex gap-2 sm:gap-4 flex-shrink-0">
        {[["S", item.s], ["M", item.m], ["XL", item.xl]].map(([label, val]) => (
          <div key={label} className="text-right w-11 sm:w-14">
            <span className="block text-[10px] uppercase tracking-wider text-[#a3a3a3]/70">{label}</span>
            <span className="font-display font-bold text-[#eab308] text-sm">{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PizzaCard() {
  return (
    <div className="rounded-2xl overflow-hidden bg-[#1a1a1a] border border-white/10 grid lg:grid-cols-12">
      <div className="lg:col-span-4 relative min-h-[240px]">
        <img src={images.pizza} alt={pizza.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#1a1a1a]" />
        <span className="absolute top-4 left-4 font-script text-2xl text-[#eab308] rotate-[-4deg]">{pizza.note}</span>
      </div>
      <div className="lg:col-span-8 p-6 md:p-8">
        <h3 className="font-display font-black text-2xl text-[#f5f5f0]">{pizza.title}</h3>
        <p className="mt-1 text-xs text-[#a3a3a3] leading-snug max-w-2xl">{pizza.desc}</p>

        <div className="mt-4">
          {sorted(pizza.items).map((it) => (
            <PizzaRow key={it.num} item={it} />
          ))}
        </div>

        <div className="mt-6 pt-5 border-t border-white/10">
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-[#e05d36]">Calzone</h4>
          <div className="mt-2">
            {sorted(pizza.calzoni).map((it) => (
              <MenuItem key={it.num} item={it} testid={`menu-pizza-calzone-${it.num}`} />
            ))}
          </div>
        </div>

        <div className="mt-5 rounded-xl bg-black/30 border border-white/10 p-4" data-testid="menu-pizza-blech">
          <h4 className="font-display font-bold text-sm uppercase tracking-wider text-[#e05d36]">{pizza.blech.title}</h4>
          <p className="mt-2 text-sm text-[#f5f5f0]">{pizza.blech.base}</p>
          <p className="mt-1.5 text-xs text-[#a3a3a3] leading-snug">{pizza.blech.plus10}</p>
          <p className="mt-1 text-xs text-[#a3a3a3] leading-snug">{pizza.blech.plus5}</p>
        </div>
      </div>
    </div>
  );
}

export default function Menu() {
  const { ref, inView } = useReveal();
  const cat = (id) => menu.find((c) => c.id === id);

  return (
    <section id="menu" data-testid="menu-section" className="relative z-10 bg-[#141414] py-20 md:py-28 border-y border-white/10">
      <div ref={ref} className="mx-auto max-w-7xl px-6 md:px-10">
        <div className={`reveal ${inView ? "in-view" : ""} max-w-2xl`}>
          <span className="text-xs uppercase tracking-[0.3em] text-[#e05d36] font-semibold">Unsere Karte</span>
          <h2 className="mt-3 font-display font-black text-4xl md:text-5xl text-[#f5f5f0]">Speisekarte</h2>
          <p className="mt-4 text-[#a3a3a3] text-lg">
            Frisch zubereitet – von Drehspieß über Pizza bis Burger. Alle Preise inkl. MwSt.
          </p>
        </div>

        {/* 01–11 Drehspieß */}
        <div className={`reveal ${inView ? "in-view" : ""} mt-12 grid gap-6`} style={{ animationDelay: "0.1s" }}>
          <FeaturedCard cat={cat("drehspiess")} image={images.doner} testidBase="menu-drehspiess" />
        </div>

        {/* 20–34 Imbiss & Burger */}
        <div className={`reveal ${inView ? "in-view" : ""} mt-6 grid md:grid-cols-2 gap-6`} style={{ animationDelay: "0.12s" }}>
          <TextCard cat={cat("imbiss")} testidBase="menu-imbiss" />
          <TextCard cat={cat("burger")} testidBase="menu-burger" />
        </div>

        {/* 35–59 Pizza */}
        <div className={`reveal ${inView ? "in-view" : ""} mt-6 grid gap-6`} style={{ animationDelay: "0.14s" }}>
          <PizzaCard />
        </div>

        {/* 61–142 + Getränke */}
        <div className={`reveal ${inView ? "in-view" : ""} mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6`} style={{ animationDelay: "0.16s" }}>
          <TextCard cat={cat("brotpide")} testidBase="menu-brotpide" />
          <TextCard cat={cat("schnitzel")} testidBase="menu-schnitzel" />
          <TextCard cat={cat("salate")} testidBase="menu-salate" />
          <TextCard cat={cat("vegetarisch")} testidBase="menu-vegetarisch" />
          <TextCard cat={cat("extras")} testidBase="menu-extras" />
          <TextCard cat={cat("getraenke")} testidBase="menu-getraenke" />
        </div>

        <div className={`reveal ${inView ? "in-view" : ""} mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 rounded-2xl bg-[#e05d36] p-6 md:p-8`}>
          <p className="font-display font-bold text-xl md:text-2xl text-black text-center sm:text-left">
            Hunger bekommen? Ruf an & bestell frisch!
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={restaurant.phoneHref}
              data-testid="menu-call-btn"
              className="inline-flex items-center gap-2 rounded-full bg-black text-[#f5f5f0] font-semibold px-6 py-3.5 whitespace-nowrap hover:-translate-y-0.5 transition-transform duration-200"
            >
              <Phone size={18} /> {restaurant.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
